import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy, signal } from '@angular/core';
import { Observable, ReplaySubject, Subject, combineLatest } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';

import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GetClient } from 'src/app/generated-client/generated-client';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/input';
import { MatChipGrid, MatChipRow, MatChipRemove, MatChipInput } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatOption } from '@angular/material/select';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'da-multi-select-client',
    templateUrl: './multi-select-client.component.html',
    styleUrl: './multi-select-client.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatFormField, MatLabel, MatChipGrid, MatChipRow, MatChipRemove, MatIcon, FormsModule, MatChipInput, MatAutocompleteTrigger, ReactiveFormsModule, MatIconButton, MatSuffix, MatAutocomplete, MatOption, AsyncPipe]
})
export class MultiSelectClientComponent implements OnInit, OnDestroy {
  @Input() label: string;
  @Input() placeholder: string;
  private _list: GetClient[] = [];
  @Input() set list(value: GetClient[]) {
    this._list = value;
    this.filterRefreshRequested.next();
  }
  get list(): GetClient[] {
    return this._list;
  }
  @Output() selectingChange = new EventEmitter<GetClient[]>();
  @ViewChild('filterInput') filterInput: ElementRef<HTMLInputElement>;
  chips = signal<GetClient[]>([]);
  filteredOptions: Observable<GetClient[]>;
  filterControl = new FormControl('');
  private filterRefreshRequested = new ReplaySubject<void>(1);
  private $destroy = new Subject<void>();

  ngOnInit(): void {
    this.filteredOptions = combineLatest([
      this.filterRefreshRequested,
      this.filterControl.valueChanges.pipe(startWith(''))
    ]).pipe(
      takeUntil(this.$destroy),
      map(([, value]) => {
        return this._filter(value || '');
      })
    );
  }

  ngOnDestroy(): void {
    this.filterRefreshRequested.complete();
    this.$destroy.next();
    this.$destroy.complete();
  }

  selectedElement(event: MatAutocompleteSelectedEvent) {
    const fruitName = event.option.viewValue;
    const selected = this.list.find((item: GetClient) => item.name === fruitName);
    if (selected) {
      this.chips.update(chips => [...chips, selected]);
      this.resultEvent();
    }
  }

  removeElement(fruit: GetClient) {
    const chips = this.chips();
    const index = chips.indexOf(fruit);
    if (index >= 0) {
      this.chips.set(chips.filter(item => item !== fruit));
      this.resultEvent();
    }
  }

  private _filter(value: string): GetClient[] {
    const filterValue = value?.toLowerCase();
    return this.list.filter(
      (option: GetClient) => option.name.toLowerCase().includes(filterValue) && this.chips().every(item => item.name !== option.name)
    );
  }

  reset(): void {
    this.chips.set([]);
    this.resultEvent();
  }

  resultEvent(): void {
    this.filterInput.nativeElement.value = '';
    this.filterControl.patchValue('');
    this.selectingChange.emit(this.chips());
  }
}
